import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, TrendingUp, Music, AlertCircle, Mail, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all'); // all, tier1, tier2
  const { user, getToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const fetchAdminData = useCallback(async () => {
    try {
      const token = getToken();
      
      // Fetch subscribers
      const subsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/subscribers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch stats
      const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!subsResponse.ok || !statsResponse.ok) {
        throw new Error('Failed to fetch admin data');
      }

      const subsData = await subsResponse.json();
      const statsData = await statsResponse.json();

      setSubscribers(subsData.subscribers);
      setStats(statsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      alert('Failed to load admin dashboard. Please try again.');
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate('/login?redirect=/admin');
      return;
    }
    if (user.role !== 'admin') {
      alert('Admin access required');
      navigate('/');
      return;
    }
    fetchAdminData();
  }, [user, navigate, authLoading, fetchAdminData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  // Filter subscribers
  const filteredSubscribers = subscribers.filter(sub => {
    if (filter === 'all') return true;
    if (filter === 'tier1') return sub.subscription?.tier === '499';
    if (filter === 'tier2') return sub.subscription?.tier === '999';
    return true;
  });

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <section className="bg-gradient-to-b from-zinc-950 to-black py-12 border-b border-red-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-center mb-2">
            ADMIN DASHBOARD
          </h1>
          <p className="text-center text-gray-400">Welcome back, {user?.name}</p>
        </div>
      </section>

      {/* Stats Cards */}
      {stats && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-6 mb-12">
              <div className="bg-gradient-to-br from-red-950/50 to-zinc-900/50 border-2 border-red-500/50 rounded-xl p-6">
                <Users className="w-8 h-8 text-red-400 mb-2" />
                <p className="text-gray-400 text-sm">Total Subscribers</p>
                <p className="text-3xl font-bold">{stats.total_subscribers}</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-950/50 to-zinc-900/50 border-2 border-green-500/50 rounded-xl p-6">
                <TrendingUp className="w-8 h-8 text-green-400 mb-2" />
                <p className="text-gray-400 text-sm">Active Subscribers</p>
                <p className="text-3xl font-bold">{stats.active_subscribers}</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-950/50 to-zinc-900/50 border-2 border-blue-500/50 rounded-xl p-6">
                <Music className="w-8 h-8 text-blue-400 mb-2" />
                <p className="text-gray-400 text-sm">Tracks Delivered</p>
                <p className="text-3xl font-bold">{stats.total_tracks_delivered}</p>
              </div>
              
              <div className="bg-gradient-to-br from-yellow-950/50 to-zinc-900/50 border-2 border-yellow-500/50 rounded-xl p-6">
                <TrendingUp className="w-8 h-8 text-yellow-400 mb-2" />
                <p className="text-gray-400 text-sm">Monthly Revenue</p>
                <p className="text-3xl font-bold">£{stats.monthly_revenue.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Subscribers List */}
      <section className="py-12 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Subscriber Requests</h2>
            
            {/* Filter Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  filter === 'all'
                    ? 'bg-red-600 text-white'
                    : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('tier1')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  filter === 'tier1'
                    ? 'bg-red-600 text-white'
                    : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                }`}
              >
                £4.99 Tier
              </button>
              <button
                onClick={() => setFilter('tier2')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  filter === 'tier2'
                    ? 'bg-red-600 text-white'
                    : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                }`}
              >
                £9.99 Tier
              </button>
            </div>
          </div>

          {filteredSubscribers.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No subscribers found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredSubscribers.map((subscriber) => {
                const tier = subscriber.subscription?.tier || '999';
                const tierName = tier === '499' ? 'VIP MEMBER (£4.99)' : 'VIP ELITE (£9.99)';
                const hasRequests = subscriber.track_selections && subscriber.track_selections.length > 0;

                return (
                  <div
                    key={subscriber.user.id}
                    className="bg-zinc-900 border border-zinc-700 rounded-xl p-6"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white">{subscriber.user.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <a
                            href={`mailto:${subscriber.user.email}`}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            {subscriber.user.email}
                          </a>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          tier === '499'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {tierName}
                        </div>
                        {subscriber.subscription?.status && (
                          <div className="mt-2">
                            <span className={`text-sm ${
                              subscriber.subscription.status === 'active'
                                ? 'text-green-400'
                                : 'text-gray-400'
                            }`}>
                              {subscriber.subscription.status.toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Track Requests */}
                    {hasRequests ? (
                      <div className="mt-4 border-t border-zinc-700 pt-4">
                        <h4 className="text-sm font-semibold text-gray-400 mb-3">REQUESTED TRACKS:</h4>
                        <div className="space-y-2">
                          {subscriber.track_selections.map((selection) => (
                            <div
                              key={selection.id}
                              className="flex justify-between items-center bg-zinc-800 rounded-lg p-3"
                            >
                              <div>
                                <p className="font-semibold text-white">{selection.track_name}</p>
                                <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>
                                    {new Date(selection.selected_at).toLocaleDateString()} at{' '}
                                    {new Date(selection.selected_at).toLocaleTimeString()}
                                  </span>
                                </div>
                              </div>
                              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                selection.delivered
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {selection.delivered ? 'Delivered' : 'Pending'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 border-t border-zinc-700 pt-4 text-center">
                        <p className="text-gray-500 text-sm">No track requests yet</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
